import { getCompressedFile } from "../shared/officeFileReader";
import { describeCompressedBytesUnavailable } from "../shared/compressedBytesSupport";

export const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** The live workbook as .xlsx bytes. All the hard parts are in the shared reader. */
export function getWorkbookAsXlsx(onProgress?: (fraction: number) => void): Promise<Blob> {
  return getCompressedFile(XLSX_MIME_TYPE, onProgress);
}

/**
 * Macro-enabled formats. getFileAsync has a long-standing reputation for returning a
 * macro-stripped package for these, and a Save that silently drops someone's VBA is
 * far worse than a Save we declined to attempt -- so we block rather than warn.
 *
 * Detection is deliberately conservative: it only fires when the extension is
 * unambiguous. If the URL is unavailable or unrecognised we allow the save, because a
 * false block on an ordinary .xlsx would break the feature for everyone.
 */
const MACRO_EXTENSIONS = [".xlsm", ".xltm", ".xlsb"];

function macroEnabledWorkbook(): boolean {
  let url: string | undefined;
  try {
    url = Office.context?.document?.url ?? undefined;
  } catch {
    return false;
  }
  if (!url) return false;

  // A checked-out workbook is opened from a signed URL, so strip the query before
  // looking at the extension -- the signature can contain anything.
  const path = url.split(/[?#]/)[0].toLowerCase();
  return MACRO_EXTENSIONS.some((extension) => path.endsWith(extension));
}

export function describeSaveUnavailable(): string | null {
  const platformReason = describeCompressedBytesUnavailable("excel");
  if (platformReason) return platformReason;

  if (macroEnabledWorkbook()) {
    return "Macro-enabled workbooks cannot be saved from the add-in, because the macros would not survive the upload. Check this workbook in from Jylo in the browser instead.";
  }

  return null;
}
